import { useState, useEffect, createContext } from "react";
import { getMainView, getViewerView, getSideViewerView, callApi, getBottomViewerView } from "./Helpers";
import { useParams } from "react-router-dom";
import Viewer from "./components/Viewer";
import MainBody from "./components/MainBody";
import Backdrop from "./components/Backdrop";
import SideViewer from "./components/SideViewer";
import ConfirmDialog from "./components/ConfirmDialog";
import { ToastContainer, toast } from 'react-toastify';
import SideBar from "./components/SideBar";
import TopBar from "./components/TopBar";
import MainMenuBtn from "./components/MainMenuBtn";
import Login from "./views/Login";
import OverlayLoader from "./components/OverlayLoader";
import MainLoader from "./components/MainLoader";
import BottomBar from "./components/BottomBar";
import BottomViewer from "./components/BottomViewer";
import { STR_COULD_NOT_FETCH_DATA, STR_PURCHASE_SUBSCRIPTION_PACKAGE } from "./Strings";
import { getCookie, setCookie } from "./Helpers";
import MobileInterface from "./MobileInterface";

export const AppContext = createContext(null);

export default function App(props) {

  const [ready, setReady] = useState(false); //for checking if app is ready

  const { _navItem, _navSubItem, _navExtraItem, _navMoreItem } = useParams(); //for routing purposes

  const [navItem, setNavItem] = useState(_navItem); //routing 
  const [navSubItem, setNavSubItem] = useState(_navSubItem); //routing
  const [navExtraItem, setNavExtraItem] = useState(_navExtraItem); //routing
  const [navMoreItem, setNavMoreItem] = useState(_navMoreItem); //routing

  const [showViewer, setShowViewer] = useState(false); //controlling the display of Viewer component
  const [viewerView, setViewerView] = useState(null); //the view to be shown in viewer

  const [showSideViewer, setShowSideViewer] = useState(false); //controlling the display of SideViewer component
  const [sideViewerView, setSideViewerView] = useState(null); //the view to be shown in viewer

  const [showBottomViewer, setShowBottomViewer] = useState(false); //controlling the display of BottomViewer component
  const [bottomViewerView, setBottomViewerView] = useState(null); //the view to be shown in viewer

  const [mainView, setMainView] = useState(null); //the view tobe shown in MainBody

  const [showOverlayLoader, setShowOverlayLoader] = useState(false); //controlling the display of OverlayLoader

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  const [confirmDialogAction, setConfirmDialogAction] = useState("");

  const [isSideBarOpen, setIsSideBarOpen] = useState(false); //controls open and close for sidebar in mobile devices

  const [auth, setAuth] = useState(false); //track user authorization status

  //currentSession
  const [currentUserData, setCurrentUserData] = useState(null);
  const [theme, setTheme] = useState(getCookie('theme') || getDefaultDeviceTheme() || 'light');
  const [language, setLanguage] = useState(getCookie('language') || 'sw');

  //App level data
  const [systemParams, setSystemParams] = useState(null);
  const [articlesFeed, setArticlesFeed] = useState(null);
  const [confessionsFeed, setConfessionsFeed] = useState(null);
  const [storiesFeed, setStoriesFeed] = useState(null);
  const [pharmacyFeed, setPharmacyFeed] = useState(null);
  const [datingFeed, setDatingFeed] = useState(null);
  const [allDoctors, setAllDoctors] = useState(null);
  const [myChatRooms, setMyChatRooms] = useState(null);
  const [myPharmacyOrders, setMyPharmacyOrders] = useState(null);
  const [myDoctorRooms, setMyDoctorRooms] = useState(null);
  const [cart, setCart] = useState(getCart());
  const [pharmacyItemForCheckout, setPharmacyItemForCheckout] = useState(null);

  async function init() {
    /**
     * Initialize the app here
     */

    setReady(false);
    await getSystemParams().then(async () => {
      await authCheck().then(async (_auth) => {
        if (_auth) {
          await getCurrentUserData();

        }
      })

      //turn off loaders after initialization
      setReady(true);
    })
  }

  async function getSystemParams() {
    return new Promise(async resolve => {
      await callApi('get-system-params', {}).then(response => {
        if (response.status === 1) {
          setSystemParams(response.data);
          resolve(response.data)
        } else {
          resolve(null)
        }
      })
    })
  }

  function navBack() {
    window.history.back();
    setShowOverlayLoader(false);
  }

  function changeLanguage() {
    if (language === 'en') {
      setLanguage('sw');
    } else {
      setLanguage('en');
    }
  }

  function changeTheme() {
    if (theme === 'light') {
      setTheme('dark');
    } else if (theme === 'dark') {
      setTheme('light');
    } else {
      setTheme('light');
    }
  }

  function tellError(msg) {
    toast.error(msg);
  }

  function tellInfo(msg) {
    toast.info(msg);
  }

  function tellWarning(msg) {
    toast.warn(msg);
  }

  function tellMessage(msg) {
    toast.success(msg);
  }

  function refresh() {
    /**
     * This function refreshes the whole app
     */
    window.location.reload(); //remember to optimize
  }

  function navTo(nav) {
    /**
     * This function handles navigation inside the app
     * Utilizing Hash based routing
     * nav is the object supporting the following keys: item, subItem, extraItem, moreItem
     */
    if (nav) {
      //..

      let url = '';
      if (nav.item) {
        url = `#/${nav.item}/`
      }

      if (nav.subItem) {
        url += `${nav.subItem}/`
      }

      if (nav.extraItem) {
        url += `${nav.extraItem}/`
      }

      if (nav.moreItem) {
        url += `${nav.moreItem}/`
      }

      window.location.href = url;
      //..
    }
  }

  function getDefaultDeviceTheme() {
    let _deviceTheme = 'light'; //default

    if(window.matchMedia) {
      if(window.matchMedia("(prefers-color-scheme: dark)").matches) {
        _deviceTheme = 'dark';
      } else if(window.matchMedia("(prefers-color-scheme: light)").matches) {
        _deviceTheme = 'light';
      }
    }

    return _deviceTheme;
  }

  async function getCurrentUserData() {
    await callApi('get-current-user-data', {}).then(response => {
      if (response.status === 1) {
        setCurrentUserData(response.data);
      }
    })
  }

  function authCheck() {
    return new Promise(async resolve => {
      await callApi('auth-check', {}).then(response => {
        if (response.status === 1) {
          setAuth(true);
          resolve(true)
        } else {
          setAuth(false);
          resolve(false);
        }
      })
    })
  }

  function activateDialog(params) {
    let {
      message,
      onConfirm
    } = params;
    setConfirmDialogAction(() => { return onConfirm });
    setConfirmDialogMessage(message)
    setShowConfirmDialog(true);
  }

  function getCart() {
    const cartString = getCookie('cart') || '';

    return decodeCart(cartString);
  }

  function addItemToCart({ itemId, itemQuantity }) {
    const _cart = getCart();

    let _itemExists = false;
    let _itemIndex = null;

    for (let i = 0; i < _cart.length; i++) {
      const _item = _cart[i];
      
      if(
        Number(_item.itemId) === Number(itemId) 
      ) {
        _itemExists = true;
        _itemIndex = i;
        break;
      }
    }

    if(_itemExists) {
      _cart[_itemIndex] = {
        itemId,
        itemQuantity,
      }
    } else {
      _cart.push({
        itemId,
        itemQuantity,
      })
    }

    //update cart
    updateCart(_cart);
  }

  function removeItemFromCart({ itemId }) {
    const _cart = getCart();

    let _itemExists = false;
    let _itemIndex = null;

    for (let i = 0; i < _cart.length; i++) {
      const _item = _cart[i];
      
      if(
        Number(_item.itemId) === Number(itemId) 
      ) {
        _itemExists = true;
        _itemIndex = i;
        break;
      }
    }

    if(_itemExists) {
      _cart.splice(_itemIndex, 1);
    } 

    //update cart
    updateCart(_cart);
  }

  function updateCart(_cartObj) {
    //store in cookie
    setCookie('cart', encodeCart(_cartObj));

    //update main cart variable
    setCart(getCart());
  }

  function decodeCart(_cartString) {
    /*
    CartString is in a form of itemId-itemQuantity|itemId-itemQuantity
    */
  
    const _cart = [];

    if(_cartString && _cartString.length > 0) {
      const _c1 = _cartString.split('|');
      if(_c1 && _c1.length > 0) {
        for (const _c2 of _c1) {
          const _c3 = _c2.split('-');
          const itemId = _c3[0];
          const itemQuantity = _c3[1];

          if(!isNaN(Number(itemId)) && !isNaN(Number(itemQuantity))) {
            _cart.push({
              itemId: Number(itemId),
              itemQuantity: Number(itemQuantity),
            })
          }
        }
      }
    }

    return _cart;
  }

  function encodeCart(_cartObj) {
    /*
    CartString is in a form of itemId-itemQuantity|itemId-itemQuantity
    */

    const cartString = _cartObj.map(item => {
      return `${item.itemId}-${item.itemQuantity}`;
    }).join('|');
    
    return cartString;
  }

  async function getArticlesFeed() {
    await callApi('get-articles-feed', {}).then(response => {
      if (response.status === 1) {
        setArticlesFeed(response.data);
      }
    })
  }

  async function getConfessionsFeed() {
    await callApi('get-confessions-feed', {}).then(response => {
      if (response.status === 1) {
        setConfessionsFeed(response.data);
      }
    })
  }

  async function getStoriesFeed() {
    await callApi('get-stories-feed', {}).then(response => {
      if (response.status === 1) {
        setStoriesFeed(response.data);
      }
    })
  }

  async function getPharmacyFeed() {
    await callApi('get-pharmacy-feed', {}).then(response => {
      if (response.status === 1) {
        setPharmacyFeed(response.data);
      }
    })
  }

  async function getDatingFeed() {
    await callApi('get-dating-feed', {}).then(response => {
      if (response.status === 1) {
        setDatingFeed(response.data);
      }
    })
  }

  async function getMyChatRooms() {
    await callApi('get-my-chatrooms', {}).then(response => {
      if (response.status === 1) {
        setMyChatRooms(response.data);
      }
    })
  }

  async function getMyPharmacyOrders() {
    await callApi('get-my-pharmacy-orders', {}).then(response => {
      if (response.status === 1) {
        setMyPharmacyOrders(response.data);
      }
    })
  }

  async function getMyDoctorRooms() {
    await callApi('get-my-doctor-rooms', {}).then(response => {
      if (response.status === 1) {
        setMyDoctorRooms(response.data);
      }
    })
  }

  async function getAllDoctors() {
    await callApi('get-all-doctors', {}).then(response => {
      if (response.status === 1) {
        setAllDoctors(response.data);
      }
    })
  }

  function getRegions() {
    return new Promise(async (resolve) => {
      await callApi("napa-get-regions", {}).then(response => {
        if (response.status === 1) {
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getDistricts(params) {
    return new Promise(async (resolve) => {
      let {
        regionId,
      } = params;

      await callApi("napa-get-districts", { regionId }).then(response => {
        if (response.status === 1) {
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getWards(params) {
    return new Promise(async (resolve) => {
      let {
        districtId,
      } = params;

      await callApi("napa-get-wards", { districtId }).then(response => {
        if (response.status === 1) {
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getStreets(params) {
    return new Promise(async (resolve) => {
      let {
        wardId,
      } = params;

      await callApi("napa-get-streets", { wardId }).then(response => {
        if (response.status === 1) {
          resolve(response.data);
          
        } else {
          resolve(null);
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
        }
      })
    })
  }

  function tellPurchaseMessage() {
    tellInfo(STR_PURCHASE_SUBSCRIPTION_PACKAGE[language]);
  }

  useEffect(() => {
    init();
  }, [])

  useEffect(() => {
    setCookie('language', language);
  }, [language])

  useEffect(() => {
    setCookie('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme])

  useEffect(() => {
    if (isSideBarOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
  }, [isSideBarOpen])

  useEffect(() => {
    setNavItem(_navItem);
    setNavSubItem(_navSubItem);
    setNavExtraItem(_navExtraItem);
    setNavMoreItem(_navMoreItem);
  }, [_navItem, _navSubItem, _navExtraItem, _navMoreItem])

  useEffect(() => {
    if (currentUserData) {
      //handle current user data changes here
      //set onesignal Id
      MobileInterface.setOneSignalExternalUserId("afya-real-user-"+currentUserData.id);
    }
  }, [currentUserData])


  useEffect(() => {

    //check for viewers
    if (navItem === 'view') {
      //activate viewer
      setShowViewer(true);
      setViewerView(getViewerView(appContext));

      //hide other viewers
      setShowSideViewer(false)
      setSideViewerView(null);

      setShowBottomViewer(false);
      setBottomViewerView(null);

    } else if (navItem === 'side-view') {
      //activate viewer
      setShowSideViewer(true);
      setSideViewerView(getSideViewerView(appContext));

      //hide other viewers
      setShowViewer(false)
      setViewerView(null);

      setShowBottomViewer(false);
      setBottomViewerView(null);

    } else if (navItem === 'bottom-view') {
      //activate viewer
      setShowBottomViewer(true);
      setBottomViewerView(getBottomViewerView(appContext));

      //do not hide other views

    } else {
      //just set normal views
      setShowViewer(false);
      setShowSideViewer(false)

      setViewerView(null);
      setSideViewerView(null);

      setShowBottomViewer(false);
      setBottomViewerView(null);


      const _mainView = getMainView(appContext)
      if (_mainView) {
        setMainView(_mainView);
      }
    }
  }, [navItem, navSubItem, navExtraItem, navMoreItem]);

  const appContext = {
    refresh,
    navTo,
    mainView,
    viewerView,
    setShowOverlayLoader,
    navItem,
    navSubItem,
    navExtraItem,
    navMoreItem,
    setShowViewer,
    showViewer,
    navBack,
    showSideViewer,
    setShowSideViewer,
    sideViewerView,
    activateDialog,
    setShowConfirmDialog,
    confirmDialogAction,
    confirmDialogMessage,
    showConfirmDialog,
    tellError,
    tellInfo,
    tellMessage,
    tellWarning,
    isSideBarOpen,
    setIsSideBarOpen,
    showOverlayLoader,
    auth,
    currentUserData,
    theme,
    language,
    articlesFeed,
    getArticlesFeed,
    storiesFeed,
    getStoriesFeed,
    getCurrentUserData,
    bottomViewerView,
    showBottomViewer,
    getRegions,
    getDistricts,
    getWards,
    getStreets,
    getDatingFeed,
    datingFeed,
    tellPurchaseMessage,
    getAllDoctors,
    allDoctors,
    myChatRooms,
    myDoctorRooms,
    getMyChatRooms,
    getMyDoctorRooms,
    systemParams,
    setLanguage,
    changeLanguage,
    setTheme,
    changeTheme,
    getConfessionsFeed,
    confessionsFeed,
    pharmacyFeed,
    getPharmacyFeed,
    cart,
    addItemToCart,
    removeItemFromCart,
    pharmacyItemForCheckout,
    setPharmacyItemForCheckout,
    myPharmacyOrders,
    getMyPharmacyOrders,
  }

  if (ready) {
    return (
      <AppContext.Provider value={appContext}>
        <MainBody />
        <TopBar />
        <BottomBar />
        <Viewer />
        <SideViewer />
        <ConfirmDialog />
        <OverlayLoader />
        <BottomViewer />
        <ToastContainer style={{ zIndex: "var(--maxIndex)", fontFamily: "FontSemiBold !important" }} position="bottom-right" />
      </AppContext.Provider>
    )
  } else {
    return (
      <AppContext.Provider value={appContext}>
        <ToastContainer style={{ zIndex: "var(--maxIndex)", fontFamily: "FontSemiBold !important" }} position="bottom-right" />
        <MainLoader />
      </AppContext.Provider>
    )
  }
}